<script setup lang="ts">
import { getLocalTypeIconSrc, fallbackTypeIcon } from "../ui/typeIcon";
import { Dropdown, Tooltip } from "floating-vue";
import { damageColors } from "../ui/damageColors";
import { computed, ref } from "vue";
import SoulMark from "./SoulMark.vue";
import AbilityLevel from "./AbilityLevel.vue";
import ShenyaoEnergy from "./ShenyaoEnergy.vue";
import { Pet } from "../core/pet";
import { getVisibleEffects, getEffectStyle, type VisibleEffect } from "../ui/visibleEffects";

const props = defineProps<{
    pet: Pet;
    pos: "left" | "right";
}>();
const isRight = ref(props.pos === "right");

const visibleStates = computed(() =>
    props.pet.异常状态.filter((state) => state.getImgUrl() !== "")
);

const visibleEffects = computed(() => getVisibleEffects(props.pet));

const effectKeys = new WeakMap<object, number>();
let nextEffectKey = 0;

function getEffectKey(item: VisibleEffect): string {
    let key = effectKeys.get(item.effect);
    if (key === undefined) {
        key = nextEffectKey++;
        effectKeys.set(item.effect, key);
    }
    return `${item.kind}-${key}`;
}

function getEffectTypeLabel(kind: VisibleEffect["kind"]): string {
    if (kind === "round") return "回合类效果";
    if (kind === "consume") return "消耗类效果";
    return "常驻类效果";
}

function getEffectName(item: VisibleEffect): string {
    return item.effect.effect.constructor.name || "未命名效果";
}

function getEffectStatus(item: VisibleEffect): string {
    if (item.remaining === null) return "持续生效";
    return `剩余 ${item.remaining} ${item.unit}`;
}

function getEffectDescription(item: VisibleEffect): string {
    return item.effect.displayDescription ?? item.effect.effect.description;
}

function getEffectAriaLabel(item: VisibleEffect): string {
    return `${getEffectTypeLabel(item.kind)}，${getEffectName(item)}，${getEffectStatus(item)}，${getEffectDescription(item)}`;
}

const defenses = computed(() => [
    { name: "护盾", icon: "/assets/damage_shield.svg", value: props.pet.护盾 },
    { name: "护罩", icon: "/assets/special_damage_shield.svg", value: props.pet.护罩 },
].filter((item) => item.value > 0));

const resistanceDetails = computed(() => [
    { name: "暴击伤害减免", value: props.pet.抗性.criticalDamage },
    { name: "固定伤害减免", value: props.pet.抗性.fixedDamage },
    { name: "百分比伤害减免", value: props.pet.抗性.percentDamage },
].map((item) => ({ ...item, percent: `${Number((item.value * 100).toFixed(2))}%` })));

const resistanceLabel = computed(() =>
    `伤害抗性，${resistanceDetails.value.map((item) => `${item.name} ${item.percent}`).join("，")}`
);

const resistancePie = computed(() => {
    let end = 0;
    const colors = damageColors;
    const slices = resistanceDetails.value.map((item, index) => {
        const start = end;
        end += item.value * 100;
        return `${colors[index]} ${start}% ${end}%`;
    });
    return `conic-gradient(${slices.join(", ")}, #64748b ${end}% 100%)`;
});
const abnormalSlots = computed(() => Array.from({ length: 3 }, (_, index) => props.pet.异常抗性[index]));
const abnormalLabel = computed(() => `异常抗性，${abnormalSlots.value.map(item => item ? `${item.state} ${Number((item.rate * 100).toFixed(2))}%` : "未配置").join("，")}`);

const hpPct = computed(() => (props.pet.当前体力 / props.pet.最大体力) * 100);
const hpClass = computed(() => {
    const r = props.pet.当前体力 / props.pet.最大体力;
    if (r <= 0.25) return "low";
    if (r <= 0.5) return "mid";
    return "high";
});
</script>

<template>
    <div :class="['pet-card', pos]">
        <!-- 头像 + 名称区 -->
        <div :class="['head-row', { right: isRight }]">
            <div class="avatar-wrap">
                <Tooltip :placement="isRight ? 'bottom-end' : 'bottom-start'">
                    <div class="avatar">
                        <img
                            :class="{ flip: isRight }"
                            :src="pet.getHeadImgUrl()"
                            alt=""
                        />
                    </div>
                    <template #popper>
                        <SoulMark :description="pet.魂印.description" />
                    </template>
                </Tooltip>
                <Dropdown
                    :placement="isRight ? 'bottom-end' : 'bottom-start'"
                >
                    <button
                        class="skin-badge"
                        :class="{ right: isRight }"
                        :title="`切换皮肤（当前：${pet.当前皮肤.name}）`"
                    >
                        <img
                            class="skin-badge-icon"
                            src="/assets/shirt.svg"
                            alt=""
                        />
                    </button>
                    <template #popper>
                        <div class="skin-list">
                            <button
                                v-for="(s, i) in pet.皮肤组"
                                :key="s.id"
                                :class="[
                                    'skin-item',
                                    { active: i === pet.当前皮肤序号 },
                                ]"
                                @click="pet.切换皮肤(i)"
                            >
                                <img :src="s.getHeadImgUrl()" alt="" />
                                <span>{{ s.name }}</span>
                            </button>
                        </div>
                    </template>
                </Dropdown>
            </div>
            <div :class="['name-group', { right: isRight }]">
                <span class="pet-name">{{ pet.名字 }}</span>
                <img :src="getLocalTypeIconSrc(pet.属性.name)" :data-fallback-src="pet.属性.getImageSrc()" @error="fallbackTypeIcon" class="type-icon" />
            </div>
        </div>

        <!-- 血条：HP标签 + 数值 + 进度条 -->
        <div :class="['hp-section', { right: isRight }]">
            <div class="hp-header">
                <span class="hp-label">HP</span>
                <span class="hp-value">{{ pet.当前体力 }} / {{ pet.最大体力 }}</span>
            </div>
            <div class="hp-track">
                <div
                    class="hp-fill"
                    :class="hpClass"
                    :style="{ width: hpPct + '%' }"
                ></div>
            </div>
        </div>

        <!-- 能力等级、护盾、护罩与抗性共用一组可换行布局。 -->
        <div :class="['ablvl-row', { right: isRight }]">
            <AbilityLevel :ab-lvl="pet.能力等级" :pos="pos" />
            <Tooltip
                v-for="item in defenses"
                :key="item.name"
                :placement="isRight ? 'bottom-end' : 'bottom-start'"
            >
                <button type="button" class="state-btn defense-btn" :aria-label="`${item.name} ${item.value}`">
                    <img :src="item.icon" alt="" />
                    <span class="effect-remaining">{{ item.value }}</span>
                </button>
                <template #popper>{{ item.name }}：{{ item.value }}</template>
            </Tooltip>
            <Tooltip :placement="isRight ? 'bottom-end' : 'bottom-start'">
                <button type="button" class="state-btn" :aria-label="resistanceLabel">
                    <span class="resistance-pie" :style="{ background: resistancePie }"></span>
                </button>
                <template #popper>
                    <div class="state-tip">
                        <span class="state-tip-name">伤害抗性</span>
                        <span v-for="item in resistanceDetails" :key="item.name" class="state-tip-rounds">
                            {{ item.name }}：{{ item.percent }}
                        </span>
                    </div>
                </template>
            </Tooltip>
            <Tooltip :placement="isRight ? 'bottom-end' : 'bottom-start'">
                <button type="button" class="state-btn" :aria-label="abnormalLabel"><img src="/assets/resistance.svg" alt="" /></button>
                <template #popper><div class="state-tip">
                    <span class="state-tip-name">异常抗性</span>
                    <span v-for="(item, index) in abnormalSlots" :key="index" class="state-tip-rounds">{{ item ? `${item.state}：${Number((item.rate * 100).toFixed(2))}%` : "未配置" }}</span>
                </div></template>
            </Tooltip>
        </div>

        <!-- 效果与异常状态 -->
        <div :class="['state-row', { right: isRight }]">
            <Tooltip
                v-for="state in visibleStates"
                :key="state.name"
                :placement="isRight ? 'bottom-end' : 'bottom-start'"
            >
                <button
                    type="button"
                    class="state-btn"
                    :aria-label="`${state.name}，剩余 ${state.remainRoundNum} 回合`"
                >
                    <img :src="state.getImgUrl()" alt="" />
                    <span class="state-rounds">{{ state.remainRoundNum }}</span>
                </button>
                <template #popper>
                    <div class="state-tip">
                        <span class="state-tip-name">{{ state.name }}</span>
                        <span class="state-tip-rounds"
                            >剩余 {{ state.remainRoundNum }} 回合</span
                        >
                    </div>
                </template>
            </Tooltip>
            <Tooltip
                v-for="item in visibleEffects"
                :key="getEffectKey(item)"
                :placement="isRight ? 'bottom-end' : 'bottom-start'"
            >
                <button
                    type="button"
                    :class="['effect-btn', `effect-${getEffectStyle(item)}`]"
                    :aria-label="getEffectAriaLabel(item)"
                >
                    <span class="effect-icon-slot" aria-hidden="true"></span>
                    <span
                        v-if="item.remaining !== null"
                        class="effect-remaining"
                        >{{ item.remaining }}</span
                    >
                </button>
                <template #popper>
                    <div class="effect-tip">
                        <span class="effect-tip-name">
                            {{ getEffectTypeLabel(item.kind) }}：{{
                                getEffectName(item)
                            }}
                        </span>
                        <span class="effect-tip-status">
                            {{ getEffectStatus(item) }}
                        </span>
                        <span class="effect-tip-description">{{ getEffectDescription(item) }}</span>
                    </div>
                </template>
            </Tooltip>
            <template v-for="buff in pet.Buff组" :key="buff.name">
                <ShenyaoEnergy v-if="buff.name === '神耀能量'" :pet="pet" :pos="pos" />
                <Tooltip v-else :placement="isRight ? 'bottom-end' : 'bottom-start'">
                    <button type="button" class="effect-btn buff-btn" :aria-label="`${buff.name} ${buff.stacks} 层，${buff.description}`">
                        <span class="effect-icon-slot" aria-hidden="true"></span>
                        <span class="effect-remaining">{{ buff.stacks }}</span>
                    </button>
                    <template #popper>
                        <div class="effect-tip">
                            <span class="effect-tip-name">{{ buff.name }}</span>
                            <span class="effect-tip-status">{{ buff.stacks }} 层</span>
                            <span class="effect-tip-description">{{ buff.description }}</span>
                        </div>
                    </template>
                </Tooltip>
            </template>
        </div>
    </div>
</template>

<style scoped>
.resistance-pie { display: block; width: calc(var(--ui-unit) * 20); height: calc(var(--ui-unit) * 20); border-radius: 50%; }
.pet-card {
    position: relative;
    box-sizing: border-box;
    min-width: 0;
    width: 100%;
    background: transparent;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    border: calc(var(--ui-unit) * 1) solid rgba(255, 255, 255, 0.08);
    border-radius: calc(var(--ui-unit) * 12);
    padding: calc(var(--ui-unit) * 10) calc(var(--ui-unit) * 12);
    box-shadow: none;
    transition: background-color 0.3s, border-color 0.3s, box-shadow 0.3s;
}

.pet-card:hover {
    background: rgba(12, 12, 24, 0.82);
    backdrop-filter: blur(calc(var(--ui-unit) * 14));
    -webkit-backdrop-filter: blur(calc(var(--ui-unit) * 14));
    box-shadow: 0 calc(var(--ui-unit) * 4) calc(var(--ui-unit) * 24) rgba(0, 0, 0, 0.4);
    border-color: rgba(255, 215, 0, 0.2);
}

/* ===== 头像行 ===== */
.head-row {
    display: flex;
    align-items: center;
    gap: calc(var(--ui-unit) * 10);
    margin-bottom: calc(var(--ui-unit) * 8);
}

.head-row.right {
    flex-direction: row-reverse;
}

.avatar-wrap {
    position: relative;
    width: calc(var(--ui-unit) * 44);
    height: calc(var(--ui-unit) * 44);
    flex-shrink: 0;
}

.avatar {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    overflow: hidden;
    border: calc(var(--ui-unit) * 2) solid rgba(255, 215, 0, 0.25);
    box-shadow: 0 0 calc(var(--ui-unit) * 12) rgba(255, 215, 0, 0.1);
    cursor: pointer;
    transition: border-color 0.2s, box-shadow 0.2s;
}

.avatar:hover {
    border-color: rgba(255, 215, 0, 0.6);
    box-shadow: 0 0 calc(var(--ui-unit) * 16) rgba(255, 215, 0, 0.25);
}

.avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.avatar .flip {
    transform: scaleX(-1);
}

/* ===== 皮肤切换角标 ===== */
.skin-badge {
    position: absolute;
    bottom: calc(var(--ui-unit) * -2);
    right: calc(var(--ui-unit) * -2);
    width: calc(var(--ui-unit) * 18);
    height: calc(var(--ui-unit) * 18);
    padding: 0;
    border: calc(var(--ui-unit) * 2) solid rgba(255, 215, 0, 0.7);
    border-radius: 50%;
    overflow: hidden;
    background: #0c0c18;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 0 calc(var(--ui-unit) * 6) rgba(255, 215, 0, 0.3);
    transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
}

.skin-badge:hover {
    border-color: #ffd700;
    box-shadow: 0 0 calc(var(--ui-unit) * 10) rgba(255, 215, 0, 0.5);
    transform: scale(1.1);
}

.skin-badge.right {
    right: auto;
    left: calc(var(--ui-unit) * -2);
}

.skin-badge-icon {
    width: calc(var(--ui-unit) * 11);
    height: calc(var(--ui-unit) * 11);
    filter: brightness(0) invert(1);
    pointer-events: none;
}

.skin-list {
    display: flex;
    flex-direction: column;
    gap: calc(var(--ui-unit) * 2);
    min-width: calc(var(--ui-unit) * 140);
    padding: calc(var(--ui-unit) * 4);
}

.skin-item {
    display: flex;
    align-items: center;
    gap: calc(var(--ui-unit) * 8);
    padding: calc(var(--ui-unit) * 4) calc(var(--ui-unit) * 6);
    border: calc(var(--ui-unit) * 1) solid transparent;
    border-radius: calc(var(--ui-unit) * 6);
    background: transparent;
    color: #e8e8e8;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
}

.skin-item:hover {
    background: rgba(255, 215, 0, 0.1);
}

.skin-item.active {
    border-color: rgba(255, 215, 0, 0.6);
    background: rgba(255, 215, 0, 0.15);
}

.skin-item img {
    width: calc(var(--ui-unit) * 26);
    height: calc(var(--ui-unit) * 26);
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
}

.skin-item span {
    font-size: calc(var(--ui-unit) * 13);
    font-weight: 600;
}

.name-group {
    display: flex;
    align-items: center;
    gap: calc(var(--ui-unit) * 8);
    flex-wrap: wrap;
}

.name-group.right {
    flex-direction: row-reverse;
}

.pet-name {
    color: #e8e8e8;
    font-weight: 700;
    font-size: calc(var(--ui-unit) * 15);
    text-shadow: 0 0 calc(var(--ui-unit) * 6) rgba(255, 255, 255, 0.1);
}

.type-icon {
    height: calc(var(--ui-unit) * 20);
    filter: drop-shadow(0 0 calc(var(--ui-unit) * 3) rgba(255, 255, 255, 0.2));
}

/* ===== 血条 ===== */
.hp-section {
    margin-bottom: calc(var(--ui-unit) * 6);
}

.hp-section.right .hp-header {
    flex-direction: row-reverse;
}

.hp-section.right .hp-track {
    display: flex;
    justify-content: flex-end;
}

.hp-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: calc(var(--ui-unit) * 3);
}

.hp-label {
    font-size: calc(var(--ui-unit) * 11);
    font-weight: 700;
    color: #a0a0b0;
    letter-spacing: calc(var(--ui-unit) * 1);
}

.hp-value {
    font-size: calc(var(--ui-unit) * 13);
    font-weight: 700;
    color: #e8e8e8;
    text-shadow: 0 0 calc(var(--ui-unit) * 6) rgba(255, 255, 255, 0.15);
}

.hp-track {
    width: 100%;
    height: calc(var(--ui-unit) * 14);
    background: rgba(0, 0, 0, 0.35);
    border-radius: calc(var(--ui-unit) * 7);
    overflow: hidden;
    border: calc(var(--ui-unit) * 1) solid rgba(255, 255, 255, 0.06);
    box-shadow: inset 0 calc(var(--ui-unit) * 1) calc(var(--ui-unit) * 3) rgba(0, 0, 0, 0.4);
}

.hp-fill {
    height: 100%;
    min-width: calc(var(--ui-unit) * 2);
    border-radius: calc(var(--ui-unit) * 7);
    transition: width 0.5s ease;
    box-shadow: inset 0 calc(var(--ui-unit) * 1) 0 rgba(255, 255, 255, 0.25);
}

.hp-fill.high {
    background: linear-gradient(180deg, #22c55e 0%, #16a34a 100%);
    box-shadow: inset 0 calc(var(--ui-unit) * 1) 0 rgba(255, 255, 255, 0.3), 0 0 calc(var(--ui-unit) * 8) rgba(34, 197, 94, 0.4);
}

.hp-fill.mid {
    background: linear-gradient(180deg, #eab308 0%, #ca8a04 100%);
    box-shadow: inset 0 calc(var(--ui-unit) * 1) 0 rgba(255, 255, 255, 0.25), 0 0 calc(var(--ui-unit) * 8) rgba(234, 179, 8, 0.4);
}

.hp-fill.low {
    background: linear-gradient(180deg, #ef4444 0%, #b91c1c 100%);
    box-shadow: inset 0 calc(var(--ui-unit) * 1) 0 rgba(255, 255, 255, 0.2), 0 0 calc(var(--ui-unit) * 10) rgba(239, 68, 68, 0.5);
    animation: pulse-low 1.2s ease-in-out infinite;
}

@keyframes pulse-low {
    0%, 100% { box-shadow: inset 0 calc(var(--ui-unit) * 1) 0 rgba(255,255,255,0.2), 0 0 calc(var(--ui-unit) * 10) rgba(239,68,68,0.5); }
    50% { box-shadow: inset 0 calc(var(--ui-unit) * 1) 0 rgba(255,255,255,0.2), 0 0 calc(var(--ui-unit) * 16) rgba(239,68,68,0.7); }
}

/* 两组信息共享排列方向与换行规则，右侧按图标顺序镜像。 */
.ablvl-row,
.state-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: calc(var(--ui-unit) * 4);
}

.ablvl-row {
    margin-bottom: calc(var(--ui-unit) * 4);
}

.ablvl-row.right,
.state-row.right {
    flex-direction: row-reverse;
}

.ablvl-row :deep(.v-popper),
.state-row :deep(.v-popper) {
    flex: 0 0 calc(var(--ui-unit) * 32);
}

.state-btn,
.effect-btn {
    position: relative;
    overflow: hidden;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 calc(var(--ui-unit) * 32);
    width: calc(var(--ui-unit) * 32);
    height: calc(var(--ui-unit) * 32);
    padding: 0;
    border: calc(var(--ui-unit) * 1) solid rgba(255, 215, 0, 0.48);
    border-radius: calc(var(--ui-unit) * 6);
    background: rgba(0, 0, 0, 0.38);
    transition: border-color 0.2s, box-shadow 0.2s, background-color 0.2s;
}

.effect-btn {
    background: rgba(0, 0, 0, 0.38);
}

.buff-btn {
    border-color: rgba(51, 192, 237, 0.88);
}

.buff-btn:hover,
.buff-btn:focus-visible {
    border-color: #8cdef5;
    background: rgba(51, 192, 237, 0.14);
    box-shadow: 0 0 calc(var(--ui-unit) * 10) rgba(51, 192, 237, 0.42);
}

.defense-btn .effect-remaining {
    font-variant-numeric: tabular-nums;
    border-color: rgba(255, 215, 0, 0.48);
}

.effect-round {
    border-color: rgba(53, 201, 111, 0.88);
}

.effect-consume {
    border-color: rgba(59, 130, 246, 0.88);
}

.effect-permanent {
    border-color: rgba(155, 93, 229, 0.88);
}

.state-btn:hover,
.state-btn:focus-visible {
    border-color: #ffd700;
    background: rgba(255, 215, 0, 0.12);
    box-shadow: 0 0 calc(var(--ui-unit) * 10) rgba(255, 215, 0, 0.32);
}

.effect-round:hover,
.effect-round:focus-visible {
    border-color: #35c96f;
    background: rgba(53, 201, 111, 0.48);
    box-shadow: 0 0 calc(var(--ui-unit) * 10) rgba(53, 201, 111, 0.42);
}

.effect-consume:hover,
.effect-consume:focus-visible {
    border-color: #3b82f6;
    background: rgba(59, 130, 246, 0.48);
    box-shadow: 0 0 calc(var(--ui-unit) * 10) rgba(59, 130, 246, 0.42);
}

.effect-permanent:hover,
.effect-permanent:focus-visible {
    border-color: #9b5de5;
    background: rgba(155, 93, 229, 0.48);
    box-shadow: 0 0 calc(var(--ui-unit) * 10) rgba(155, 93, 229, 0.42);
}

.state-btn:focus-visible,
.effect-btn:focus-visible {
    outline: calc(var(--ui-unit) * 2) solid rgba(255, 255, 255, 0.85);
    outline-offset: calc(var(--ui-unit) * 2);
}

.effect-icon-slot {
    display: block;
    width: calc(var(--ui-unit) * 22);
    height: calc(var(--ui-unit) * 22);
    pointer-events: none;
}

.state-btn img {
    object-fit: contain;
    width: 100%;
    height: 100%;
    border-radius: calc(var(--ui-unit) * 6);
    filter: drop-shadow(0 0 calc(var(--ui-unit) * 3) rgba(0, 0, 0, 0.5));
    pointer-events: none;
}

.state-rounds {
    position: absolute;
    right: calc(var(--ui-unit) * 1);
    bottom: calc(var(--ui-unit) * 1);
    min-width: calc(var(--ui-unit) * 12);
    height: calc(var(--ui-unit) * 12);
    line-height: calc(var(--ui-unit) * 12);
    padding: 0 calc(var(--ui-unit) * 2);
    border-radius: calc(var(--ui-unit) * 4) 0 calc(var(--ui-unit) * 4) 0;
    background: rgba(255, 215, 0, 0.9);
    color: #1a1a2e;
    font-size: calc(var(--ui-unit) * 9);
    font-weight: 800;
    text-align: center;
    pointer-events: none;
}

.effect-remaining {
    position: absolute;
    right: calc(var(--ui-unit) * 1);
    bottom: calc(var(--ui-unit) * 1);
    min-width: calc(var(--ui-unit) * 12);
    height: calc(var(--ui-unit) * 12);
    line-height: calc(var(--ui-unit) * 12);
    padding: 0 calc(var(--ui-unit) * 2);
    border: calc(var(--ui-unit) * 1) solid rgba(255, 255, 255, 0.42);
    border-radius: calc(var(--ui-unit) * 4) 0 calc(var(--ui-unit) * 4) 0;
    background: rgba(10, 12, 22, 0.9);
    color: #f4f6fb;
    font-size: calc(var(--ui-unit) * 9);
    font-weight: 800;
    text-align: center;
    pointer-events: none;
}

.effect-round .effect-remaining {
    border-color: rgba(53, 201, 111, 0.82);
}

.effect-consume .effect-remaining {
    border-color: rgba(59, 130, 246, 0.82);
}

.effect-tip,
.state-tip {
    display: flex;
    flex-direction: column;
    gap: calc(var(--ui-unit) * 2);
}

.effect-tip-name,
.state-tip-name {
    font-weight: 700;
}

.effect-tip-status,
.effect-tip-description,
.state-tip-rounds {
    font-size: calc(var(--ui-unit) * 12);
    opacity: 0.85;
}


</style>
